import React, { useEffect } from 'react';
import { NumberField } from '@flos/react-ui';
import useAppState from '../../state/appState';
import {
    Currencyfield as CurrencyfieldType,
    FormValues,
    FormConfig
} from '@topdanmark-onlinesolutions/claim-form-types';
import { getIsValid, getValidationErrors } from '../../validators/validator-mapping';
import { ElementValue } from '../../types/form-values';
import { ClaimFormSubmitFieldValue } from '../../types/claim-form-submit-field-value';
import { SummaryRowTemplate } from '../../helpers/templates';
import { ElementValueHuman } from '../../types/element-values';
import { isRequired } from '../../helpers/isRequired';
import { getElementFromElementId } from '../../helpers/getElementFromElementId';
import { getValue } from '../values/Get';
import { getClaimFormSubmitValue } from '../../helpers/getClaimFormSubmitValue';

const Currencyfield = ({ elementId, caption, placeholder, helpText }: CurrencyfieldType) => {
    const formConfig = useAppState((state) => state.formConfig);
    const formValues = useAppState((state) => state.formValues);
    const setFormValue = useAppState((state) => state.setFormValue);

    const value = Currencyfield.getValueOrDefault(formValues, elementId);

    const [errorMessage, setErrorMessage] = React.useState('');

    const [fieldHasHadFocusAtLeastOnce, setFieldHasHadFocusAtLeastOnce] = React.useState(false);

    const doValidation = () => {
        if (!formConfig) {
            return;
        }

        if (!fieldHasHadFocusAtLeastOnce) {
            return;
        }

        const errorMessages = getValidationErrors(formConfig, formValues, elementId);
        setErrorMessage(errorMessages[0]);
    };

    useEffect(() => {
        doValidation();
        // eslint-disable-next-line
    }, [value]);

    const isRequiredField = isRequired(formConfig, elementId);

    const isValid = Currencyfield.isValidField(formConfig, formValues, elementId, fieldHasHadFocusAtLeastOnce);

    return (
        <>
            {caption && <h6>{caption}</h6>}
            <NumberField
                value={value}
                onValueChange={(values) => {
                    const value = values.formattedValue;
                    setFormValue(elementId, value);
                }}
                onBlur={() => {
                    doValidation();
                }}
                onFocus={() => {
                    setFieldHasHadFocusAtLeastOnce(true);
                }}
                isValid={isValid}
                errorText={errorMessage}
                id={elementId}
                key={elementId}
                label={placeholder}
                helpText={helpText}
                withFormatting
                decimalsAllowed={2}
                required={isRequiredField}
                aria-label={caption}
                data-testid="currencyfield"
                inputMode="decimal"
            />
        </>
    );
};

Currencyfield.ResumePage = (formConfig: FormConfig, formValues: FormValues, elementId: string): JSX.Element => {
    const element = getElementFromElementId(formConfig, elementId) as CurrencyfieldType;
    const { summaryCaption } = element;

    const value = getValue(formConfig, formValues, elementId);

    // Happens when the user has not filled
    // out previous pages but somehow navigated to the resume page
    if (value === undefined) {
        return <></>;
    }

    return <SummaryRowTemplate elementId={elementId} caption={summaryCaption} value={`${value} kr.`} />;
};

Currencyfield.getValueSubmit = (
    formValues: FormValues,
    elementId: string,
    label: string
): ClaimFormSubmitFieldValue => {
    const valueObj = Currencyfield.getValueOrDefault(formValues, elementId);
    const valueStr = Currencyfield.getValueHuman(formValues, elementId);
    const valueId = { string: valueObj };

    return getClaimFormSubmitValue('currencyfield', elementId, label, valueStr, valueId);
};

Currencyfield.getValue = (formValues: FormValues, elementId: string): ElementValue => {
    return formValues[elementId];
};

Currencyfield.getValueOrDefault = (formValues: FormValues, elementId: string) => {
    const value = Currencyfield.getValue(formValues, elementId);
    if (value === undefined) {
        return '';
    }
    return value;
};

Currencyfield.getValueHuman = (formValues: FormValues, elementId: string): ElementValueHuman => {
    const valueObj = Currencyfield.getValueOrDefault(formValues, elementId);
    const valueStr = valueObj && valueObj + ' kr.';

    return valueStr;
};

Currencyfield.isValid = (formConfig: FormConfig, formValues: FormValues, elementId: string): boolean => {
    return getIsValid(formConfig, formValues, elementId);
};

Currencyfield.isValidField = (
    formConfig: FormConfig | undefined,
    formValues: FormValues,
    elementId: string,
    fieldHasHadFocusAtLeastOnce: boolean
): boolean | undefined => {
    if (!formConfig) {
        return undefined;
    }

    if (!fieldHasHadFocusAtLeastOnce) {
        return undefined;
    }

    //const value = Currencyfield.getValue(formValues, elementId);

    // When the user has not typed anything,
    // undefined is returned to prevent
    // showing red errors all over as soon
    // as he sees the form
    // if (value === '' || value === undefined) {
    //     return undefined;
    // }

    const isValid = Currencyfield.isValid(formConfig, formValues, elementId);

    return isValid;
};

export default Currencyfield;
